import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Eye,
    FileText,
    ExternalLink,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import contentPagesService from '../../services/contentPages';

export default function AdminContentPages() {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        try {
            setLoading(true);
            const response = await contentPagesService.getAll();
            // Handle both array response and object-with-data-property response
            const data = Array.isArray(response) ? response : (response?.data || []);
            setPages(data);
        } catch (error) {
            console.error("Fetch pages error:", error);
            toast.error('Failed to fetch pages');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this page?')) return;
        try {
            await contentPagesService.delete(id);
            toast.success('Page deleted successfully');
            setPages(prev => prev.filter(p => p._id !== id));
        } catch (error) {
            toast.error('Failed to delete page');
        }
    };

    const filteredPages = Array.isArray(pages) ? pages.filter(p =>
        p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.slug?.toLowerCase().includes(searchQuery.toLowerCase())
    ) : [];

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Content Pages</h1>
                    <p className="text-slate-500">Manage About Us, Privacy Policy, and other content pages.</p>
                </div>
                <Button onClick={() => navigate('/admin/pages/new')} className="gap-2">
                    <Plus size={18} />
                    Create New Page
                </Button>
            </div>

            <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                        placeholder="Search pages by title or slug..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="w-[300px]">Title</TableHead>
                            <TableHead>Slug</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Last Updated</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        <span>Loading pages...</span>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : filteredPages.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                                    <FileText className="mx-auto mb-2 opacity-20" size={48} />
                                    <p>No pages found matching your search.</p>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredPages.map((page) => (
                                <TableRow key={page._id} className="hover:bg-slate-50/50 transition-colors">
                                    <TableCell className="font-semibold text-slate-900">
                                        {page.title}
                                    </TableCell>
                                    <TableCell className="text-slate-600 font-mono text-xs">
                                        /{page.slug}
                                    </TableCell>
                                    <TableCell>
                                        {page.is_active ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                                                <CheckCircle size={12} /> Published
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-600 border border-slate-100">
                                                <XCircle size={12} /> Draft
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {page.show_in_header && <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] rounded uppercase font-bold tracking-wider">Header</span>}
                                            {page.show_in_footer && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] rounded uppercase font-bold tracking-wider">Footer</span>}
                                            {!page.show_in_header && !page.show_in_footer && <span className="text-xs text-slate-400">Hidden</span>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-slate-500 text-xs">
                                        {new Date(page.updated_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-slate-400 hover:text-indigo-600"
                                                onClick={() => window.open(`/pages/${page.slug}`, '_blank')}
                                                title="View Live"
                                            >
                                                <ExternalLink size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-slate-400 hover:text-indigo-600"
                                                onClick={() => navigate(`/admin/pages/edit/${page._id}`)}
                                                title="Edit Page"
                                            >
                                                <Edit2 size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-slate-400 hover:text-rose-600"
                                                onClick={() => handleDelete(page._id)}
                                                title="Delete Page"
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
